import React, { useCallback, useReducer } from 'react';
import { createGameCategoryReducer } from './createGameCategoryReducer';
import {
	CreateGameCategoryContext,
	createGameCategoryInitialState,
	TCreateGameCategoryVisibleForm,
	TTypeOfCreateGameCategoryModalWindow,
} from './createGameCategoryContext';
import { CreateGameCategoryActionTypes } from './createGameCategoryActions';
import { IItemForGetShortInfo, TLanguageIDs, ICreateGameCategory, IGameCategoryTranslation } from 'types';

const CreateGameCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createGameCategoryReducer, createGameCategoryInitialState);

	const changeVisibleForm = useCallback((value: TCreateGameCategoryVisibleForm) => {
		dispatch({
			type: CreateGameCategoryActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: CreateGameCategoryActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateGameCategoryActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				languages: value,
			},
		});
	}, []);

	const setIDsLanguages = useCallback(
		(value: TLanguageIDs[]) => {
			dispatch({
				type: CreateGameCategoryActionTypes.SET_IDs_LANGUAGES,
				payload: {
					IDsLanguages: value,
				},
			});

			const isNewLanguageAdded = value.length > state.translations.length;
			if (isNewLanguageAdded) {
				const existingTranslationIDs = state.translations.map((el) => el.languageId);
				const newTranslationID = value.find((el) => !existingTranslationIDs.includes(el));
				if (newTranslationID) {
					const newTranslation: IGameCategoryTranslation = {
						languageId: newTranslationID,
						name: '',
					};
					const updatedTranslations = [...state.translations, newTranslation];
					dispatch({
						type: CreateGameCategoryActionTypes.SET_TRANSLATION_FIELDS,
						payload: {
							updatedTranslations,
						},
					});
				}
			} else {
				const updatedTranslations = state.translations.filter((el) => value.includes(el.languageId));
				dispatch({
					type: CreateGameCategoryActionTypes.SET_TRANSLATION_FIELDS,
					payload: {
						updatedTranslations,
					},
				});
			}
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: ICreateGameCategory | null) => {
		dispatch({
			type: CreateGameCategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				data: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateGameCategoryModalWindow) => {
		dispatch({
			type: CreateGameCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateGameCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				translationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateGameCategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const resetFields = useCallback(() => dispatch({ type: CreateGameCategoryActionTypes.RESET_FIELDS }), []);

	return (
		<CreateGameCategoryContext.Provider
			value={{
				changeVisibleForm,
				changeName,
				setFetchedLanguages,
				setIDsLanguages,
				setDataForSubmit,
				changeTypeOfModalWindow,
				setTranslationTab,
				changeNameTranslation,
				resetFields,
				visibleForm: state.visibleForm,
				name: state.name,
				fetchedLanguages: state.fetchedLanguages,
				IDsSelectedLanguages: state.IDsSelectedLanguages,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
			}}>
			{children}
		</CreateGameCategoryContext.Provider>
	);
};

export default CreateGameCategoryState;
